import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import { getAccessToken } from "../awsAuth";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000";

const SubscriptionContext = createContext({
  loading: true,
  error: null,

  plan: "free",
  planUntil: 0,
  premiumUntil: 0,

  isActive: false,
  isFree: true,
  isPlus: false,
  isBusiness: false,

  entitlements: {
    maxActiveStories: 1,
    externalAdsEnabled: true,
    plusBadge: false,
    basicStats: false,
    businessStats: false,
    businessModules: false,
    aiSearch: false,
    prioritySupport: false,
  },

  trial: {
    eligible: false,
    active: false,
    used: false,
    plan: null,
    startedAt: null,
    until: null,
  },

  refresh: async () => {},
});

function normalizePlan(plan) {
  const p = String(plan || "free").toLowerCase();

  if (p === "pro" || p === "premium") return "plus";
  if (["free", "plus", "business"].includes(p)) return p;

  return "free";
}

function getDefaultState() {
  return {
    plan: "free",
    planUntil: 0,
    premiumUntil: 0,

    isActive: false,
    isFree: true,
    isPlus: false,
    isBusiness: false,

    entitlements: {
      maxActiveStories: 1,
      externalAdsEnabled: true,
      plusBadge: false,
      basicStats: false,
      businessStats: false,
      businessModules: false,
      aiSearch: false,
      prioritySupport: false,
    },

    trial: {
      eligible: false,
      active: false,
      used: false,
      plan: null,
      startedAt: null,
      until: null,
    },
  };
}

export const SubscriptionProvider = ({ children }) => {
  const { userId, isLoggedIn } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [serverState, setServerState] = useState(getDefaultState);

  const refresh = useCallback(async () => {
    if (!isLoggedIn || !userId) {
      setServerState(getDefaultState());
      setError(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const token = await getAccessToken();

      if (!token) {
        throw new Error("No token");
      }

      const res = await fetch(`${API_BASE}/api/subscription/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const data = await res.json();

      const plan = normalizePlan(data?.plan);
      const planUntil = Number(data?.planUntil || data?.premiumUntil || 0);
      const premiumUntil = Number(data?.premiumUntil || data?.planUntil || 0);

      const isBusiness = !!data?.isBusiness || plan === "business";
      const isPlus = !!data?.isPlus || plan === "plus" || plan === "business";
      const isActive = !!data?.isActive || isPlus || isBusiness;

      setServerState({
        plan,
        planUntil,
        premiumUntil,

        isActive,
        isFree: plan === "free",
        isPlus,
        isBusiness,

        entitlements: {
          ...getDefaultState().entitlements,
          ...(data?.entitlements || {}),
        },

        trial: {
          ...getDefaultState().trial,
          ...(data?.trial || {}),
        },
      });
      setError(null);
    } catch (e) {
      console.warn("[Subscription] /api/subscription/me failed:", e?.message || e);
      setServerState(getDefaultState());
      setError(e?.message || "failed");
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, userId]);

  useEffect(() => {
    if (isLoggedIn === null) return;

    refresh();
  }, [isLoggedIn, userId, refresh]);

  useEffect(() => {
    const onFocus = () => {
      refresh();
    };

    window.addEventListener("focus", onFocus);

    return () => {
      window.removeEventListener("focus", onFocus);
    };
  }, [refresh]);

  const value = useMemo(
    () => ({
      loading,
      error,

      plan: serverState.plan,
      planUntil: serverState.planUntil,
      premiumUntil: serverState.premiumUntil,

      isActive: serverState.isActive,
      isFree: serverState.isFree,
      isPlus: serverState.isPlus,
      isBusiness: serverState.isBusiness,

      entitlements: serverState.entitlements,
      trial: serverState.trial,

      refresh,
    }),
    [loading, error, serverState, refresh]
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => useContext(SubscriptionContext);